import { useEffect, useRef } from 'react';
import {
  focusOn,
  isAnyModalOpen,
  shouldFocusAlert,
} from '../util/profile-information-field/fieldControllerFocus';
import {
  FIELD_UPDATE_EFFECTS,
  getFieldUpdateEffects,
} from '../util/profile-information-field/getFieldUpdateEffects';

/**
 * Runs the field controller's post-update side effects: the close-edit-view
 * timer, focus management, and the forceEditView success callback.
 *
 * The decisions live in getFieldUpdateEffects; this hook only executes them.
 *
 * PORT NOTE: there is no dependency array. componentDidUpdate ran after every
 * re-render regardless of which props changed, and an effect with no deps is
 * the faithful equivalent — the previous-value comparison inside decides
 * whether anything actually happens.
 */
export const useFieldControllerEffects = ({
  fieldName,
  transaction,
  transactionRequest,
  showEditView,
  showRemoveModal,
  showValidationView,
  showUpdateSuccessAlert,
  showErrorAlert,
  forceEditView,
  useVapSvcShowErrors,
  successCallback,
  onCloseModal,
}) => {
  const closeTimeoutRef = useRef(null);
  const prevRef = useRef(null);
  // Callbacks are read at effect time so they never need to be dependencies.
  const callbacksRef = useRef(null);

  callbacksRef.current = { successCallback, onCloseModal };

  useEffect(() => {
    const prev = prevRef.current;
    const next = {
      fieldName,
      transaction,
      transactionRequest,
      showEditView,
      showRemoveModal,
      showValidationView,
      showUpdateSuccessAlert,
      showErrorAlert,
      forceEditView,
      useVapSvcShowErrors,
      hasSuccessCallback: typeof successCallback === 'function',
    };

    prevRef.current = next;

    // PORT NOTE: componentDidUpdate never ran for the initial render. Without
    // this guard a field mounted with a transaction already in the store would
    // schedule a close-edit-view timer the class never scheduled.
    if (!prev) {
      return;
    }

    const effects = getFieldUpdateEffects({
      prev,
      next,
      isModalOpen: isAnyModalOpen(),
    });

    effects.forEach(effect => {
      switch (effect.type) {
        case FIELD_UPDATE_EFFECTS.SCHEDULE_CLOSE_TIMER:
          closeTimeoutRef.current = setTimeout(
            () => callbacksRef.current.onCloseModal?.(),
            effect.delay,
          );
          break;

        case FIELD_UPDATE_EFFECTS.CLEAR_CLOSE_TIMER:
          clearTimeout(closeTimeoutRef.current);
          break;

        case FIELD_UPDATE_EFFECTS.CLOSE_MODAL:
          callbacksRef.current.onCloseModal?.();
          break;

        case FIELD_UPDATE_EFFECTS.FOCUS:
          focusOn(effect);
          break;

        case FIELD_UPDATE_EFFECTS.FOCUS_ALERT_WHEN_NO_MODAL:
          shouldFocusAlert().then(shouldFocus => {
            if (shouldFocus) {
              focusOn(effect);
            }
          });
          break;

        case FIELD_UPDATE_EFFECTS.SUCCESS_CALLBACK:
          callbacksRef.current.successCallback?.();
          break;

        /* c8 ignore next 2 */
        default:
          break;
      }
    });
  });

  // PORT NOTE: the class called setTimeout without ever clearing it on
  // unmount, so the timer could fire openModal(null) against a field that had
  // already left the page.
  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);
};
